import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type { SessionDetail } from '../domain/session-detail.model';
import { emptyMutation, initialState, type MutationKind } from './session-detail.state';

/**
 * Admin Session Detail slice. Reducers are pure state transitions only; every
 * side effect (the initial load, the 3s poll, the pipeline actions, the
 * action→reload) lives in the saga, which dispatches these `load*` / `mutation*`
 * transitions as it progresses.
 */
const slice = createSlice({
  name: 'sessionDetail',
  initialState,
  reducers: {
    // ── Load lifecycle ──
    // `loadStarted` keeps the existing `session` so a poll/action refresh does not
    // blank the page (the page guards on `session`, not on `loadStatus`).
    loadStarted: (state) => {
      state.loadStatus = 'loading';
    },
    loadSucceeded: (state, action: PayloadAction<SessionDetail>) => {
      state.session = action.payload;
      state.loadStatus = 'loaded';
    },
    loadFailed: (state) => {
      state.loadStatus = 'failed';
    },

    // ── Action (mutation) lifecycle: pause / resume / retry / cancel / reset / retryErrors ──
    mutationStarted: (state, action: PayloadAction<MutationKind>) => {
      state.mutation = { status: 'pending', kind: action.payload, error: null };
    },
    mutationSucceeded: (state, action: PayloadAction<MutationKind>) => {
      state.mutation = { status: 'succeeded', kind: action.payload, error: null };
    },
    mutationFailed: (state, action: PayloadAction<{ kind: MutationKind; message: string }>) => {
      state.mutation = {
        status: 'failed',
        kind: action.payload.kind,
        error: action.payload.message,
      };
    },
    /** Acknowledge a settled action (page calls after showing the snackbar). */
    mutationCleared: (state) => {
      state.mutation = emptyMutation;
    },

    /** Reset everything to the initial state (on mount + unmount). */
    resetForm: () => initialState,
  },
});

export const sessionDetailActions = slice.actions;
export const sessionDetailReducer = slice.reducer;
